import type { BlogPost } from '../types';

export const post: BlogPost = {
  "id": "102",
  "slug": "how-to-get-better-at-geography-games",
  "title": "How to Get Better at Satellite Geography Games",
  "excerpt": "Compare terrain, fields, and coastlines without turning one clue into a certainty. A practical way to review guesses and learn from the places you confuse.",
  "date": "2026-03-16",
  "category": "Community",
  "image": "https://images.unsplash.com/photo-1446776811953-b23d57bd21aa?w=800&q=80",
  "readTime": "3 min",
  "content": [
    {
      "type": "paragraph",
      "text": "A green forest, a dry plain, or a grid of fields can occur in many countries. To make a useful guess, look for a combination of features and check whether they fit the same place."
    },
    {
      "type": "paragraph",
      "text": "Start with the whole image before zooming into small details. Note the terrain, any water, and how much of the scene is built up or farmed. Then choose a couple of plausible regions to compare."
    },
    {
      "type": "heading",
      "text": "Use colour with care"
    },
    {
      "type": "paragraph",
      "text": "Colour can help you describe land cover, but it is a weak shortcut to a country. Green may indicate a forest or an irrigated field. Brown may be bare soil, dormant vegetation, or a recently harvested crop."
    },
    {
      "type": "paragraph",
      "text": "Look at texture alongside colour. A smooth field divided by straight boundaries differs from an uneven tree canopy. Season, lighting, and image processing can change the appearance, so check another feature before committing to a climate zone."
    },
    {
      "type": "heading",
      "text": "Follow the terrain and water"
    },
    {
      "type": "paragraph",
      "text": "Trace the direction of ridges and river valleys. Notice whether mountains rise beside a coast or above a wide plain. These relationships can be more useful than the colour of an individual slope."
    },
    {
      "type": "paragraph",
      "text": "For a river mouth, examine the channel pattern and the surrounding land. The Nile Delta's cultivated land contrasts with the adjacent desert. A close crop of one field would hide that context, so make sure the feature you rely on is actually visible."
    },
    {
      "type": "heading",
      "text": "Read fields without overclaiming"
    },
    {
      "type": "paragraph",
      "text": "Circular fields often indicate centre-pivot irrigation. That identifies a farming method, not a country. The method occurs in many parts of the world, including the United States, Brazil, and Saudi Arabia."
    },
    {
      "type": "paragraph",
      "text": "Compare the circles with the terrain, nearby settlements, and road pattern. Long strips or small irregular fields can also suggest lines of enquiry, but an image alone does not establish the ownership history or income of the people farming them."
    },
    {
      "type": "heading",
      "text": "Check the coastline you can see"
    },
    {
      "type": "paragraph",
      "text": "If there is a coast, trace its shape before choosing a country. Look for a broad bay, offshore islands, or a mountain range close to the water. Compare that arrangement with your candidate location."
    },
    {
      "type": "paragraph",
      "text": "If there is no coast in the image, leave island countries in consideration. A crop of an island's interior may contain no visible water at all. The edge of the image is a limit on your evidence, not a geographic boundary."
    },
    {
      "type": "heading",
      "text": "Use infrastructure to test a guess"
    },
    {
      "type": "paragraph",
      "text": "Road junctions, runways, and harbours give you shapes to compare against a candidate location. A grid of streets or a large airport is too common to settle the question alone. Its position relative to water and terrain may help more."
    },
    {
      "type": "paragraph",
      "text": "Before placing a guess, check orientation and distance on the map. Recognising the right country still leaves a large area to choose from."
    },
    {
      "type": "heading",
      "text": "Review a mistake while it is fresh"
    },
    {
      "type": "paragraph",
      "text": "Write one sentence after the reveal: what did you notice, and what did you infer from it? “I saw circular fields and assumed the United States” identifies a rule you can revise."
    },
    {
      "type": "paragraph",
      "text": "Compare the revealed location with the region you guessed at a similar scale. Find a visible difference, or acknowledge that the frame did not contain enough evidence to distinguish them. For your next round, try making one candidate guess and one alternative before choosing."
    }
  ],
  "tldr": "Start with broad terrain and land cover, then compare two plausible regions using smaller details. Treat colour and field patterns as clues rather than country identifiers. After each reveal, record what supported or contradicted your guess."
};
